/**
 * Lógica de la ficha del contrato del protocolo, sin React ni store: estado,
 * vigencia y datos de liquidación listos para mostrar.
 */

export type StatusTone = 'success' | 'warning' | 'danger' | 'informative';

const STATUS: Record<number, { label: string; tone: StatusTone }> = {
    1: { label: 'Activo', tone: 'success' },
    2: { label: 'Inactivo', tone: 'warning' },
    3: { label: 'Suspendido', tone: 'warning' },
    4: { label: 'Expirado', tone: 'danger' },
    5: { label: 'Cancelado', tone: 'danger' },
    6: { label: 'Terminado', tone: 'informative' }
};

export const contractStatusMeta = (status: number | undefined): { label: string; tone: StatusTone } =>
    (status !== undefined && STATUS[status]) || { label: 'No definido', tone: 'informative' };

// ── Vigencia ──────────────────────────────────────────────────────────────

/** Días antes del fin en los que el contrato se marca como por vencer. */
export const EXPIRING_DAYS = 60;

export type ValidityState =
    | 'undefined'   // sin fechas
    | 'notStarted'  // empieza más adelante
    | 'openEnded'   // empezó y no tiene fecha de fin
    | 'active'
    | 'expiring'    // quedan EXPIRING_DAYS días o menos
    | 'expired';

export interface Validity {
    state: ValidityState;
    /** Porcentaje transcurrido, 0 a 100. Sólo con inicio y fin. */
    percent?: number;
    daysLeft?: number;
    daysToStart?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Días calendario entre dos fechas, sin que la hora ni el cambio de horario muevan el resultado. */
const dayNumber = (date: Date): number => Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS);

const toDate = (value: Date | string | undefined | null): Date | undefined => {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const buildValidity = (
    start: Date | string | undefined | null,
    end: Date | string | undefined | null,
    today: Date = new Date()
): Validity => {
    const from = toDate(start);
    const to = toDate(end);
    const now = dayNumber(today);

    if (!from && !to) return { state: 'undefined' };

    if (from && dayNumber(from) > now) {
        return { state: 'notStarted', daysToStart: dayNumber(from) - now };
    }

    if (!to) return { state: 'openEnded' };

    const daysLeft = dayNumber(to) - now;
    let percent: number | undefined;
    if (from) {
        const total = dayNumber(to) - dayNumber(from);
        percent = total <= 0 ? 100 : Math.min(100, Math.max(0, Math.round(((now - dayNumber(from)) / total) * 100)));
    }

    if (daysLeft < 0) return { state: 'expired', percent: 100, daysLeft };
    return { state: daysLeft <= EXPIRING_DAYS ? 'expiring' : 'active', percent, daysLeft };
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** La frase corta que acompaña la barra de vigencia. */
export const describeValidity = (validity: Validity): string => {
    switch (validity.state) {
        case 'undefined':
            return 'Sin fechas de vigencia';
        case 'notStarted':
            return `Empieza en ${plural(validity.daysToStart ?? 0, 'día', 'días')}`;
        case 'openEnded':
            return 'Vigente, sin fecha de fin';
        case 'expired':
            return `Venció hace ${plural(Math.abs(validity.daysLeft ?? 0), 'día', 'días')}`;
        default:
            return validity.daysLeft === 0
                ? 'Vence hoy'
                : `Quedan ${plural(validity.daysLeft ?? 0, 'día', 'días')}`;
    }
};

// ── Liquidación ───────────────────────────────────────────────────────────

const SCHEDULE: Record<number, { label: string; unit?: [string, string] }> = {
    1: { label: 'Diaria', unit: ['día', 'días'] },
    2: { label: 'Semanal', unit: ['semana', 'semanas'] },
    3: { label: 'Mensual', unit: ['mes', 'meses'] },
    4: { label: 'Pago único' },
    5: { label: 'A demanda' }
};

/** "Mensual", o "Cada 3 meses" cuando la frecuencia es mayor a uno. Undefined si no está definida. */
export const paymentScheduleLabel = (type: number | undefined, frequency: number | undefined): string | undefined => {
    const schedule = type !== undefined ? SCHEDULE[type] : undefined;
    if (!schedule) return undefined;
    if (schedule.unit && (frequency ?? 0) > 1) {
        return `Cada ${frequency} ${schedule.unit[1]}`;
    }
    return schedule.label;
};
